import type { AnalyticsType } from '@ria-client/segment/types';
import type { CtaType } from '@ria-client/supergraph/roundup/types';
import type { ElementType } from 'react';
import { groupedByH2ContentAndWrapItWithContainer } from './lib/group-by-h2';
import { Accordion } from './nodes/accordion';
import { AccordionItem } from './nodes/accordion-item';
import { AnchorLink } from './nodes/anchor-link';
import { AnchorLinkTarget } from './nodes/anchor-link-target';
import { AnnouncementBanner } from './nodes/announcement-banner';
import { BackToTopLink } from './nodes/back-to-top-link';
import { BainGenericContainer } from './nodes/bain-generic-container';
import { SCButton } from './nodes/button';
import { CalculatedFieldsForm } from './nodes/calculated-fields-form';
import { Citation } from './nodes/citation/citation';
import { Column } from './nodes/column';
import { Columns } from './nodes/columns';
import { ComponentValidationError } from './nodes/component-validation-error';
import { Container } from './nodes/container';
import { ContentHeading } from './nodes/content-heading';
import { ContentImage } from './nodes/content-image';
import { ContentRibbon } from './nodes/content-ribbon';
import { ContentSection } from './nodes/content-section';
import { Divider } from './nodes/divider';
import { Document } from './nodes/document';
import { Experiment } from './nodes/experiment';
import { FadeContainer } from './nodes/fade-container';
import { FAQ } from './nodes/faq';
import { FinancialAdvisorsAdvisorProductCard } from './nodes/financial-advisors-advisor-product-card';
import { FinancialAdvisorsComparisonTable } from './nodes/financial-advisors-comparison-table';
import { FinancialAdvisorsProductCard } from './nodes/financial-advisors-product-card';
import { FinancialAdvisorsRoundupSections } from './nodes/financial-advisors-roundup-sections';
import { FinancialAdvisorsSummaryTable } from './nodes/financial-advisors-summary-table';
import { FirstPersonPerspective } from './nodes/first-person-perspective';
import { FirstPersonPerspectiveItem } from './nodes/first-person-perspective-item';
import { IconCardList } from './nodes/icon-card-list';
import { Infogram } from './nodes/infogram';
import { Li } from './nodes/li';
import { Link } from './nodes/link';
import { List } from './nodes/list';
import { MarketplaceField } from './nodes/marketplace-field';
import { MarketplaceProductCard } from './nodes/marketplace-product-card';
import { MatchDynamicBanner } from './nodes/match-dynamic-banner';
import { MiniProductCard } from './nodes/mini-product-card';
import { MissingNode } from './nodes/missing-node';
import { NerdTip } from './nodes/nerd-tip';
import { NerdwalletAdvisorsBanner } from './nodes/nerdwallet-advisors-banner';
import { NerdyTakeaway } from './nodes/nerdy-takeaway';
import { NerdyTip } from './nodes/nerdy-tip';
import { SMBIllustrationCardList } from './nodes/smb-illustration-card-list';
import { SMBInfoGatheringCta } from './nodes/smb-info-gathering-cta';
import { SMBProsAndConsTable } from './nodes/smb-pros-and-cons-table';
import { SMBWhyTrustNerdWallet } from './nodes/smb-why-trust';
import { SMBWhyTrustNerdWalletBullet } from './nodes/smb-why-trust-bullet';
import { Spacer } from './nodes/spacer';
import { SCStarRating } from './nodes/star-rating';
import { StickyContainer } from './nodes/sticky-container';
import { TableOfContents } from './nodes/table-of-contents';
import { TableOfContentsTarget } from './nodes/table-of-contents-target';
import { Tabs } from './nodes/tabs';
import { TikTok } from './nodes/tiktok';
import { Tooltip } from './nodes/tooltip';
import { Type } from './nodes/type';
import { Well } from './nodes/well';
import { WPBlock } from './nodes/wp-block';
import { WPContentPageHeader } from './nodes/wp-content-page-header';
import { WPDynamicPlacement } from './nodes/wp-dynamic-placement';
import { WPHouseAd } from './nodes/wp-house-ad';
import { WPInlineBlock } from './nodes/wp-inline-block';
import { WPPostList } from './nodes/wp-post-list';
import { WPTable } from './nodes/wp-table';
import { WpVideo } from './nodes/wp-video';
import { isStructuredContent, type StructuredContent } from './types';

// Direct dispatch. One renderer, no registry (NWA-3978). Entries are added by
// the tasks that implement each node.
// Module-level test seam for stub registration; Task 18 also reads it for node coverage.
export const NODES: Record<string, ElementType> = {
  Accordion,
  AccordionItem,
  AnchorLink,
  AnchorLinkTarget,
  BackToTopLink,
  BainGenericContainer,
  Button: SCButton,
  CalculatedFieldsForm,
  Citation,
  ComponentValidationError,
  Column,
  Columns,
  Container,
  ContentImage,
  ContentRibbon,
  ContentSection,
  ContentHeading,
  Document,
  Divider,
  Experiment,
  FadeContainer,
  FAQ,
  FinancialAdvisorsAdvisorProductCard,
  FinancialAdvisorsProductCard,
  FinancialAdvisorsRoundupSections,
  FinancialAdvisorsSummaryTable,
  FirstPersonPerspective,
  FirstPersonPerspectiveItem,
  Infogram,
  FinancialAdvisorsComparisonTable,
  IconCardList,
  Li,
  List,
  Link,
  MarketplaceField,
  MarketplaceProductCard,
  // Named query is gone in prod ("not found"); seo-pages renders an empty wrapper. NWA-4214.
  MarketplaceComparisonTable: () => null,
  MiniProductCard,
  NerdTip,
  NerdyTakeaway,
  NerdyTip,
  NerdwalletAdvisorsBanner,
  NerdwalletAdvisorsAnnouncementBanner: AnnouncementBanner,
  NerdwalletAdvisorsMatchDynamicBanner: MatchDynamicBanner,
  SMBIllustrationCardList,
  SMBInfoGatheringCta,
  SMBProsAndConsTable,
  SMBWhyTrustNerdWallet,
  SMBWhyTrustNerdWalletBullet,
  Spacer,
  StarRating: SCStarRating,
  StickyContainer,
  TableOfContents,
  TableOfContentsTarget,
  Tabs,
  TikTok,
  Tooltip,
  Type,
  Well,
  WPBlock,
  WPContentPageHeader,
  WPDynamicPlacement,
  WPHouseAd,
  WPInlineBlock,
  WPPostList,
  WPTable,
  Video: WpVideo,
};

export const NODE_TYPES = Object.keys(NODES);

interface ProductCardBottomLineCtaData {
  cta: CtaType;
  ctaAnalytics: AnalyticsType;
}

export interface StructuredContentRendererProps {
  doc: StructuredContent | StructuredContent[];
  productCardBottomLineCtaData?: ProductCardBottomLineCtaData;
  tableOfContentsEnabled?: boolean;
  /**
   * True when this renderer call is already inside a `Type` node. Replaces the
   * legacy client-side TypeContext so the renderer can stay a server component.
   */
  nested?: boolean;
}

function addProductCardBottomLineCtaData(
  value: unknown,
  data: NonNullable<StructuredContentRendererProps['productCardBottomLineCtaData']>,
): unknown {
  if (Array.isArray(value)) {
    return value.map(child => addProductCardBottomLineCtaData(child, data));
  }
  if (!value || typeof value !== 'object') return value;
  if (isStructuredContent(value)) {
    return {
      ...value,
      ...(value.type === 'Link' && { productCardBottomLineCtaData: data }),
      props: addProductCardBottomLineCtaData(value.props, data),
    };
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, child]) => [
      key,
      addProductCardBottomLineCtaData(child, data),
    ]),
  );
}

export function StructuredContentRenderer({
  doc,
  productCardBottomLineCtaData,
  tableOfContentsEnabled = false,
  nested = false,
}: StructuredContentRendererProps) {
  if (typeof doc === 'string') {
    return <p className="typography-body">{doc}</p>;
  }

  const trackedDoc = productCardBottomLineCtaData
    ? (addProductCardBottomLineCtaData(doc, productCardBottomLineCtaData) as
        StructuredContent | StructuredContent[])
    : doc;
  // TOC items are populated page-wide during hydration (supergraph populatePageTableOfContents).
  const normalizedDoc: StructuredContent | StructuredContent[] =
    doc && !Array.isArray(doc) && typeof doc !== 'string' && tableOfContentsEnabled
      ? groupedByH2ContentAndWrapItWithContainer(doc)
      : trackedDoc;

  return (
    <>
      {[normalizedDoc].flat().map((child, idx) => {
        if (typeof child === 'string') {
          return (
            <span key={`sc-doc-text-${idx}`} className="typography-body">
              {child}
            </span>
          );
        }
        if (!child) return null;

        const Node = NODES[child.type];
        if (Node) return <Node {...child} nested={nested} key={idx} />;

        console.warn(
          `Structured Content Renderer missing component for type ${child.type}`,
          child.props,
        );
        return <MissingNode type={child.type} key={idx} />;
      })}
    </>
  );
}
